<template>
  <router-view v-if="routerAlive"/>
</template>

<script>
import _ from 'lodash'
import { getLanguageCode } from '@/i18n'
import { mapActions, mapGetters } from 'vuex'
import { needIndexRedirectRouteName } from './route'
import { getFilePath } from '@/utils/qiniu'
import { deviceType } from '@/utils/common'

export default {
  metaInfo () {
    return {
      link: this.linkList,
      style: this.styleList
    }
  },
  data () {
    return {
      routerAlive: true,
      invitationInfo: {},
      loginCodeInfo: null,
      loginCode: null,
      linkList: [],
      styleList: []
    }
  },
  computed: {
    ...mapGetters('company', ['domainCompany']),
    invitationId () {
      return this.$route.params.invitationId
    },
    isNestle () {
      return this.invitationInfo.isNestle
    },
    headerImage () {
      const isMobile = deviceType.isMobile
      let imagePath = null
      if (isMobile) {
        imagePath = this.invitationInfo.mobile_image
      } else {
        imagePath = this.invitationInfo.pc_image
      }
      return getFilePath(imagePath) || ''
    },
    logoPath () {
      return this.invitationInfo.logo
    },
    currentAccount () {
      return this.getCurrentAccount()
    }
  },
  beforeCreate () {
    this.$i18n.locale = getLanguageCode()
  },
  async created () {
    this.handleEventInBus()
    this.$eventBus.$emit('showLoading', true)
    this.showOnboardingPage(false)
    try {
      this.refreshPageCss()
      await this.initData()
    } finally {
      this.showOnboardingPage(true)
      this.$eventBus.$emit('showLoading', false)
    }
  },
  methods: {
    ...mapActions('account', ['getSessionAccountInfo']),
    ...mapActions('onboarding', ['checkOnboardingPermission', 'getLoginCodeInfo', 'getInvitationInfo']),
    ...mapGetters('account', ['getCurrentAccount']),
    refreshPageCss () {
      if (this.$route.name === 'onboardingSiteLayout') {
        this.linkList = []
      } else {
        this.linkList = [
          {
            rel: 'stylesheet',
            href: '/vue-static/bootstrap/bootstrap.min.css'
          },
          {
            rel: 'stylesheet',
            href: `${process.env.VUE_APP_BASE_URL}static/style/mobile_web/style_common.css`
          },
          {
            rel: 'stylesheet',
            href: `${process.env.VUE_APP_BASE_URL}static/style/mobile_web/style_zh.css`
          }
        ]
      }
    },
    async initData () {
      let hasPassword = true
      let loginCode = this.$route.query.loginCode
      if (loginCode) {
        try {
          const resp = await this.getLoginCodeInfo({ loginCode })
          this.loginCodeInfo = resp
          hasPassword = resp.has_password
        } catch (err) {
          loginCode = null
        }
      } else {
        this.loginCodeInfo = null
      }
      let hasLogin = this.currentAccount && this.currentAccount.id
      if (!hasPassword && loginCode) {
        hasLogin = false
      } else {
        const accountInfo = await this.getSessionAccountInfo()
        hasLogin = accountInfo && accountInfo.id
      }
      await this.refreshInvitationInfo()
      if (!hasLogin) {
        const isEmployee = this.loginCodeInfo?.is_employee
        const nextPage = {
          path: `/passport/${isEmployee ? 'employee' : 'candidate'}`,
          query: {
            back: this.$route.fullPath,
            company_id: this.invitationInfo?.company_id
          }
        }
        if (hasPassword) {
          nextPage.path += '/login'
        } else {
          nextPage.path += `/create-password/${this.loginCodeInfo.reset_code}`
        }
        this.$router.push(nextPage)
        return
      }
      const skipRedirect = [
        `/onboarding/${this.invitationId}/forget-password`,
        `/onboarding/${this.invitationId}/reset-password`
      ]
      if (skipRedirect.includes(this.$route.path)) {
        return
      }
      let targetPath = `/onboarding/${this.invitationId}`
      if (hasLogin) {
        if (needIndexRedirectRouteName.includes(this.$route.name)) {
          targetPath = this.getOnboardingHomePath()
        } else {
          targetPath = null
        }
      }
      if (targetPath) {
        if (targetPath === this.$route.fullPath) {
          this.routerAlive = false
          this.$nextTick(() => {
            this.routerAlive = true
          })
        } else {
          this.$router.push(targetPath)
        }
      } else {
        if (this.$route.query.loginCode) {
          this.$router.replace(this.$route.path)
        }
      }
      await this.checkPermission(this.invitationId)
    },
    handleEventInBus () {
      this.$eventBus.$on('showOnboardingPage', this.showOnboardingPage)
      this.$eventBus.$on('changeOnboardingTheme', (color) => {
        this.setOnboardingThemeStyle(color)
      })
      this.$eventBus.$on('openOnboardingQuestionnairePage', (openWithNewWindow) => {
        const pagePath = `/onboarding/${this.invitationId}/questionnaire/${this.invitationInfo.questionnaire_id}`
        if (openWithNewWindow) {
          window.open(pagePath)
        } else {
          this.$router.push(pagePath)
        }
      })
    },
    setOnboardingThemeStyle (color) {
      this.styleList = [
        {
          vmid: 'custom-onboarding-theme',
          innerHTML: `
            .custom-bg-color.main-background-style {
              background-color: ${color};
            }
            .custom-border-color.main-border-style {
              border-color: ${color};
            }
            .custom-color.dropdown-item:hover,
            .custom-color.day-cell-available:hover,
            .custom-color.selected-day-cell,
            .custom-color.main-color-style {
              color: ${color};
            }
          `
        }
      ]
    },
    async refreshInvitationInfo () {
      const resp = await this.getInvitationInfo(this.invitationId)
      if (this.domainCompany && this.domainCompany.children) {
        if (!_.includes(this.domainCompany.children, resp.company_id)) {
          window.location.href = '/'
        }
      }
      const hasCustom = resp.has_custom
      resp.isNestle = hasCustom && hasCustom === 'Nestle'
      if (resp.isNestle) {
        resp.microsite_url = null
      }
      this.invitationInfo = resp
      if (!this.invitationInfo.isNestle && this.invitationInfo.custom_color && this.invitationInfo.custom_color.startsWith('#')) {
        this.setOnboardingThemeStyle(this.invitationInfo.custom_color)
      }
    },
    getOnboardingHomePath () {
      let homePath = `/onboarding/${this.invitationId}`
      if (this.isNestle) {
        homePath += '/nestle/landing'
      } else if (this.invitationInfo.microsite_url) {
        homePath += `/site/${this.invitationInfo.microsite_url}`
      } else {
        homePath += `/questionnaire/${this.invitationInfo.questionnaire_id}`
      }
      return homePath
    },
    showOnboardingPage (isShow) {
      this.routerAlive = isShow
    },
    async checkPermission (invitationId) {
      if (this.$route.meta.noOnboardingPermissionCheck) {
        return
      }
      this.$eventBus.$emit('showLoading', true)
      this.routerAlive = false
      try {
        const resp = await this.checkOnboardingPermission(invitationId)
        if (resp) {
          this.routerAlive = true
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async reloadMicroSite () {
      this.routerAlive = false
      await this.$nextTick()
      this.routerAlive = true
    }
  },
  watch: {
    $route (newVal) {
      this.checkPermission(this.invitationId)
      this.refreshPageCss()
    }
  }
}
</script>

<style lang="scss" scoped>

</style>
